/**
 * Score Theme Configuration
 *
 * Centralised color palette and scoring logic for score-related components.
 * All thresholds, labels, colors, gradients, and chip styles are fully
 * configurable via REACT_APP_SCORE_* environment variables.
 * See .env.sample for the complete list and default values.
 */

/** Configurable score thresholds */
export const SCORE_THRESHOLD_GOOD = parseFloat(
  process.env.REACT_APP_SCORE_THRESHOLD_GOOD || "0.8",
);
export const SCORE_THRESHOLD_FAIR = parseFloat(
  process.env.REACT_APP_SCORE_THRESHOLD_FAIR || "0.5",
);

/** Configurable score labels */
export const SCORE_LABEL_GOOD =
  process.env.REACT_APP_SCORE_LABEL_GOOD || "Good";
export const SCORE_LABEL_FAIR =
  process.env.REACT_APP_SCORE_LABEL_FAIR || "Fair";
export const SCORE_LABEL_POOR =
  process.env.REACT_APP_SCORE_LABEL_POOR || "Poor";

/** Primary score-level colors */
export const SCORE_COLOR_GOOD =
  process.env.REACT_APP_SCORE_COLOR_GOOD || "#ffb300";
export const SCORE_COLOR_FAIR =
  process.env.REACT_APP_SCORE_COLOR_FAIR || "#e65100";
export const SCORE_COLOR_POOR =
  process.env.REACT_APP_SCORE_COLOR_POOR || "#c62828";

/** Background gradients for the score gauge area */
export const SCORE_BG_GOOD =
  process.env.REACT_APP_SCORE_BG_GOOD ||
  "linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%)";
export const SCORE_BG_FAIR =
  process.env.REACT_APP_SCORE_BG_FAIR ||
  "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)";
export const SCORE_BG_POOR =
  process.env.REACT_APP_SCORE_BG_POOR ||
  "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)";

/** Impact bar gradients (low / medium / high severity) */
export const LOW_BAR_GRADIENT =
  process.env.REACT_APP_SCORE_LOW_BAR_GRADIENT ||
  "linear-gradient(90deg, #ffca28, #ffb300)";
export const MED_BAR_GRADIENT =
  process.env.REACT_APP_SCORE_MED_BAR_GRADIENT ||
  "linear-gradient(90deg, #ffa726, #fb8c00)";
export const HIGH_BAR_GRADIENT =
  process.env.REACT_APP_SCORE_HIGH_BAR_GRADIENT ||
  "linear-gradient(90deg, #ef5350, #d32f2f)";

/** Chip styling (low / medium / high severity) */
export const LOW_CHIP_BG = process.env.REACT_APP_SCORE_LOW_CHIP_BG || "#fff8e1";
export const LOW_CHIP_COLOR =
  process.env.REACT_APP_SCORE_LOW_CHIP_COLOR || "#f57f17";
export const MED_CHIP_BG = process.env.REACT_APP_SCORE_MED_CHIP_BG || "#fff3e0";
export const MED_CHIP_COLOR =
  process.env.REACT_APP_SCORE_MED_CHIP_COLOR || "#e65100";
export const HIGH_CHIP_BG =
  process.env.REACT_APP_SCORE_HIGH_CHIP_BG || "#ffebee";
export const HIGH_CHIP_COLOR =
  process.env.REACT_APP_SCORE_HIGH_CHIP_COLOR || "#c62828";

/** Metric tile accent colors */
export const METRIC_ACCENT =
  process.env.REACT_APP_SCORE_METRIC_ACCENT || "#1565c0";
export const LOW_IMPACT_COLOR =
  process.env.REACT_APP_SCORE_LOW_IMPACT_COLOR || "#ffb300";
export const HIGH_IMPACT_COLOR =
  process.env.REACT_APP_SCORE_HIGH_IMPACT_COLOR || "#c62828";
/** Slider accent color for scoring config forms */
export const SLIDER_ACCENT =
  process.env.REACT_APP_SCORE_SLIDER_ACCENT || "#1565c0";

/** Helper: resolve primary color from a numeric score */
export const getScoreColor = (score: number): string => {
  if (score >= SCORE_THRESHOLD_GOOD) return SCORE_COLOR_GOOD;
  if (score >= SCORE_THRESHOLD_FAIR) return SCORE_COLOR_FAIR;
  return SCORE_COLOR_POOR;
};

/** Helper: resolve background gradient from a numeric score */
export const getScoreBg = (score: number): string => {
  if (score >= SCORE_THRESHOLD_GOOD) return SCORE_BG_GOOD;
  if (score >= SCORE_THRESHOLD_FAIR) return SCORE_BG_FAIR;
  return SCORE_BG_POOR;
};

/** Helper: human-readable quality label */
export const getScoreLabel = (score: number): string => {
  if (score >= SCORE_THRESHOLD_GOOD) return SCORE_LABEL_GOOD;
  if (score >= SCORE_THRESHOLD_FAIR) return SCORE_LABEL_FAIR;
  return SCORE_LABEL_POOR;
};

/** Helper for ScoreBadge (accepts nullable score) */
export const getScoreColorNullable = (
  score: number | null | undefined,
): string => {
  if (score === null || score === undefined) return "#9e9e9e";
  return getScoreColor(score);
};

/** Job status → MUI color mapping */
export const getStatusColor = (
  status: string,
): "success" | "warning" | "error" | "default" => {
  if (status === "completed") return "success";
  if (status === "completed_with_errors") return "warning";
  if (status === "failed") return "error";
  return "default";
};

/** Pipeline display labels */
export const PIPELINE_LABELS: Record<string, string> = {
  validation: "Validation",
  comparison: "Comparison",
  research_query: "Research Query (GRQ)",
};

/** Pipeline accent colors */
export const PIPELINE_COLORS: Record<string, string> = {
  validation: "#4caf50",
  comparison: "#2196f3",
  research_query: "#ff9800",
};

/** Format 0–1 score as percent string */
export const formatPercent = (value: number): string =>
  `${(value * 100).toFixed(1)}%`;
